/**
 * @typedef {{
 * width?: number,
 * height?: number,
 * x?: number,
 * y?: number,
 * scale?: number,
 * ratio?: number[],
 * center?: boolean,
 * padding?: number,
 * horizontalOffset?: number,
 * verticalOffset?: number,
 * boxSelector?: string
 * }} TConfig
 * @description Configuration object for the RegionOfInterest component, defining initial dimensions, positioning, and scaling behavior.
 * @property {number} [width=300] The base width of the region before scaling is applied (in pixels).
 * @property {number} [height=0] The calculated height of the region. (Excluded from constructor config).
 * @property {number} [x=0] The fixed left position (x-coordinate) of the region relative to the container, used when `center` is false.
 * @property {number} [y=0] The fixed top position (y-coordinate) of the region relative to the container, used when `center` is false.
 * @property {number} [scale=1.5] The factor by which the base `width` and calculated `height` are multiplied.
 * @property {number[]} [ratio=[1, 1]] The aspect ratio as an array [widthRatio, heightRatio] used to calculate the region's height.
 * @property {boolean} [center=true] If true, the region is centered horizontally and vertically within the container.
 * @property {number} [padding=20] Minimum distance (in pixels) the region must maintain from the container edges.
 * @property {number} [horizontalOffset=-60] A horizontal offset applied when `center` is true.
 * @property {number} [verticalOffset=0] A vertical offset applied when `center` is true.
 * @property {string} [boxSelector='.box'] CSS selector used to find the region element (`_regionElement`) inside the container.
 */

/**
 * @class
 * @classdesc Manages and controls the dimensions and position of a 'Region of Interest' element
 * within a container, handling scaling, aspect ratio, centering, and padding.
 * @example
 * // HTML setup:
 * // <div id="container" style="width: 600px; height: 400px;">
 * //   <div class="box" style="position: absolute;"></div>
 * // </div>
 *
 * const wrapper = document.getElementById('container');
 * const region-of-interest = new RegionOfInterest(wrapper, {
 * width: 200,
 * scale: 2,
 * ratio: [16, 9],
 * padding: 10
 * });
 *
 * region-of-interest.__registerResizeHandler(dimensions => {
 * console.log('Region resized:', dimensions); // { width: 400, height: 225, x: 100, y: 88 }
 * });
 *
 * // Update configuration later
 * region-of-interest.__applyConfiguration({ scale: 1.0, horizontalOffset: 0 });
 */
export class RegionOfInterest {
  /**
   * @type {HTMLDivElement}
   * @private
   * @description The main container element relative to which the region is positioned and sized.
   */
  _containerElement;

  /**
   * @type {HTMLDivElement}
   * @private
   * @description The actual HTML element representing the region of interest (the 'box').
   */
  _regionElement;

  /**
   * @type {function(dimensions: {width: number, height: number, x: number, y: number}): void}
   * @private
   * @description Callback function executed after the region's size and position are calculated and applied.
   */
  _onResizeCallback;

  /**
   * @type {TConfig}
   * @private
   * @description The merged and active configuration settings for the region.
   */
  _configuration = {
    width: 300,
    height: 0,
    x: 0,
    y: 0,
    center: true,
    scale: 1.5,
    ratio: [1, 1],
    padding: 20,
    horizontalOffset: 0,
    verticalOffset: 0,
    boxSelector: '.box',
  };

  /**
   * @returns {{width: number, height: number, x: number, y: number}} The current dimensions and position of the region element.
   * @public
   * @description Retrieves the current calculated width, height, left (x), and top (y) values
   * of the `_regionElement` directly from its inline CSS style, parsed as integers.
   * @example
   * const dims = region-of-interest.__dimensions; // { width: 300, height: 300, x: 150, y: 100 }
   */
  get __dimensions() {
    const { width, height, left: x, top: y } = this._regionElement.style;
    return { width: parseInt(width || '0'), height: parseInt(height || '0'), x: parseInt(x || '0'), y: parseInt(y || '0') };
  }

  /**
   * @type {TConfig}
   * @public
   * @description The merged and active configuration settings for the region.
   */
  get __configuration() {
    return Object.freeze({ ...this._configuration });
  }

  /**
   * @constructor
   * @param {HTMLDivElement} wrapper The DOM element that acts as the container for the region.
   * @param {Omit<TConfig, "height">} [config] Configuration options to override the defaults.
   * @throws {Error} If the `wrapper` element is not provided.
   * @throws {Error} If the required region element (via `boxSelector`) is not found inside the wrapper.
   */
  constructor(wrapper, config) {
    this._containerElement = wrapper;
    if (!this._containerElement) {
      throw new Error('Container element is missing. Please provide a valid HTMLDivElement as the first argument (wrapper).');
    }

    this.__applyConfiguration(config);

    const selector = this._configuration.boxSelector;
    this._regionElement = this._containerElement.querySelector(selector);
    if (!this._regionElement) {
      throw new Error(`Region element not found. The container must contain an element matching the selector "${selector}".`);
    }
  }

  /**
   * @param {function(dimensions: {width: number, height: number, x: number, y: number}): void} callback The function to execute on resize/recalculation.
   * @public
   * @description Registers a single callback function that will be executed whenever the region's
   * size or position is updated and applied to the DOM.
   * @example
   * region-of-interest.__registerResizeHandler((d) => console.log('New Dims:', d));
   */
  __registerResizeHandler(callback) {
    if (!callback || this._onResizeCallback) {
      return;
    }
    this._onResizeCallback = callback;
  }

  /**
   * @param {Omit<TConfig, "height">} [config] New configuration options to merge with the current settings.
   * @public
   * @description Merges the provided configuration object with the existing settings and immediately
   * triggers the recalculation and application of styles.
   * @example
   * region-of-interest.__applyConfiguration({ scale: 1.0, center: false, x: 50, y: 50 });
   */
  __applyConfiguration(config) {
    this._configuration = Object.assign(this._configuration, config || {});
    this._calculateAndApplyStyles();
  }

  /**
   * @private
   * @description Internal method to calculate the region's final size and position based on
   * configuration, container size, and constraints (padding, ratio, scale).
   * It then applies the calculated styles to the `_regionElement` and calls the callback.
   */
  _calculateAndApplyStyles() {
    // Calculate height based on ratio and width
    this._configuration.height = Math.ceil((this._configuration.width / this._configuration.ratio[0]) * this._configuration.ratio[1]);

    const {
      //
      width,
      height,
      x,
      y,
      scale,
      center,
      padding,
      horizontalOffset,
      verticalOffset,
    } = this._configuration;

    const scaledWidth = width * scale;
    const scaledHeight = height * scale;

    // Get container dimensions
    const parentWidth = this._containerElement.getBoundingClientRect().width;
    const parentHeight = this._containerElement.getBoundingClientRect().height;

    // Apply min(scaledSize, parentSize) constraints
    const _width = Math.ceil(Math.min(scaledWidth, parentWidth));
    const _height = Math.ceil(Math.min(scaledHeight, parentHeight));

    // Calculate position (centered or fixed)
    let _x = Math.ceil(center ? (parentWidth - _width) / 2 + horizontalOffset : x);
    let _y = Math.ceil(center ? (parentHeight - _height) / 2 + verticalOffset : y);

    // Apply padding constraints
    if (_x < padding) _x = padding;
    if (_y < padding) _y = padding;

    // Only apply styles if position is valid (avoids applying negative or zero positions in edge cases)
    if (_x > padding && _y > padding) {
      const style = this._regionElement.style;

      style.width = _width + 'px';
      style.height = _height + 'px';
      style.left = _x + 'px';
      style.top = _y + 'px';

      this._onResizeCallback?.({ width: _width, height: _height, x: _x, y: _y });
    }
  }
}
